<script setup lang="ts">
  import { computed, h, nextTick, onBeforeUnmount, onMounted, provide, reactive, ref } from 'vue'
  import { pendingRefocusKey } from '@/shared/models/pendingRefocus'
  import Splitter from 'primevue/splitter'
  import SplitterPanel from 'primevue/splitterpanel'
  import { Card, Button, message, Drawer, Modal, Spin } from 'ant-design-vue'
  import {
    PlusOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    HolderOutlined,
    CodeOutlined,
    FileSearchOutlined
  } from '@ant-design/icons-vue'
  import { useUser } from '@/composables/useUser'
  import { Loader2, Trash2, CloudCog, Download } from 'lucide-vue-next'
  import { watch } from 'vue'
  import { defaultMessageDurationSeconds } from '@/utils'
  import FullScreenModal from '@/components/FullScreenModal.vue'
  import DataViewer from '@/components/DataViewer.vue'
  import { debounceTime, Subject } from 'rxjs'
  import { InjectionService } from '@/shared/services/InjectionService'
  import { SchemaStoreService } from '@/shared/services/SchemaStoreService'
  import { AnalysisWizardService } from '../AnalysisWizard/services/AnalysisWizardService'
  import { WizardStepStatusType } from '@/shared/Wizard/models/WizardStep'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { AplosFile } from '../FileManager/models/AplosFile'
  import { QueueExecutionResponse } from '@/shared/Executions/models/QueueExecutionResponse'
  import { useAppLayoutMode } from '@/composables/useAppLayoutMode'
  import { AppLayoutMode } from '@/models/AppLayoutMode'
  import FileManager from '../FileManager/components/FileManager.vue'
  import { Execution } from '@/shared/Executions/models/Execution'
  import { ExecutionStatusType } from '@/shared/Executions/models/ExecutionStatusType'
  import { AdvancedAnalysis } from './models/AdvancedAnalysis'
  import { AdvancedAnalysisSchema } from './models/AdvancedAnalysisSchema'
  import {
    AdvancedAnalysisApiService,
    IAdvancedAnalysisApiService
  } from './services/AdvancedAnalysisApiService'
  import { AdvancedAnalysisService } from './services/AdvancedAnalysisService'
  import { AdvancedAnalysisJsonParsingService } from './services/AdvancedAnalysisJsonParsingService'
  import { AdvancedAnalysisSearchService } from './services/AdvancedAnalysisSearchService'
  import { AdvancedAnalysisUiService } from './services/AdvancedAnalysisUiService'
  import { AdvancedAnalysisSchemaUiFieldGroup } from './models/AdvancedAnalysisSchemaUi'
  import { GetExecutionOutputPackageArgs } from '@/shared/Executions/models/GetExecutionOutputPackageArgs'
  import { downloadFile, extractFilename } from '@/utils/download'
  import { toast } from 'vue-sonner'
  import { useRouter } from 'vue-router'
  import AdvancedAnalysisConfiguration from './components/AdvancedAnalysisConfiguration.vue'
  import { AdvancedAnalysisTypeGuardService } from './services/AdvancedAnalysisTypeGuardService'
  import JsonAdvancedAnalysisConfiguration from './components/JsonAdvancedAnalysisConfiguration.vue'
  import { AdvancedAnalysisJsonConfigurationService } from './services/AdvancedAnalysisJsonConfigurationService'
  import AdvancedAnalysisSelectionModal from './components/AdvancedAnalysisSelectionModal.vue'
  import AdvancedAnalysisJsonManagement from './components/AdvancedAnalysisJsonManagement.vue'
  import {
    ExecutionApiService,
    IExecutionApiService
  } from '@/shared/Executions/services/ExecutionApiService'
  import ExecutionStatusComponent from '@/shared/Executions/components/ExecutionStatusComponent.vue'
  import { AdvancedAnalysisWizardModel } from '../AdvancedAnalysisWizard/models/AdvancedAnalysisWizardModel'
  import { AdvancedAnalysisWizardNavigationService } from '../AdvancedAnalysisWizard/services/AdvancedAnalysisWizardNavigationService'
  import { CdnSchemaFactory } from '@/shared/CdnSchema/services/CdnSchemaFactory'
  import { CdnSchemaType } from '@/shared/CdnSchema/models/CdnSchemaType'

  interface IAdvancedAnalysisProps {
    analysisFile?: AplosFile
    advancedAnalysis?: AdvancedAnalysis // flattened
    advancedAnalysisExecution?: Execution // stores last known queued execution for this step
    fileSelectionDisabled?: boolean
    mode?: 'multiconfig' | 'singleconfig'
    showJsonTab?: boolean
    wizardModel?: AdvancedAnalysisWizardModel
    showLoading?: boolean
  }

  const cdnSchemaFactory: CdnSchemaFactory = InjectionService.injectStrict(
    CdnSchemaFactory.injectableKey
  )

  const wizardNavigationService: AdvancedAnalysisWizardNavigationService =
    InjectionService.injectStrict(AdvancedAnalysisWizardNavigationService.injectableKey)

  const advancedAnalysisApiService: IAdvancedAnalysisApiService = InjectionService.injectStrict(
    AdvancedAnalysisApiService.injectableKey
  )

  const advancedAnalysisJsonParsingService: AdvancedAnalysisJsonParsingService =
    InjectionService.injectStrict(AdvancedAnalysisJsonParsingService.injectableKey)

  const executionApiService: IExecutionApiService = InjectionService.injectStrict(
    ExecutionApiService.injectableKey
  )

  const router = useRouter()

  const customSchemaStore = SchemaStoreService.useCustomSchemaStore()

  const props = defineProps<IAdvancedAnalysisProps>()

  const emit = defineEmits<{
    (e: 'update:advancedAnalysis', value: AdvancedAnalysis | undefined): void
    (e: 'update:advancedAnalysisExecution', value?: Execution): void
  }>()

  const pendingRefocus = ref<{ levelFieldKey: string; cursor: number | null } | null>(null)
  provide(pendingRefocusKey, pendingRefocus)

  const executionStatusComponent = ref<InstanceType<typeof ExecutionStatusComponent> | null>(null)

  const execution = ref<Execution | undefined>(props.advancedAnalysisExecution)

  watch(
    () => props.advancedAnalysisExecution,
    (val) => {
      execution.value = val
    },
    { immediate: true }
  )

  const auth = useUser()

  const isInitializing = ref<boolean>(false)
  const isDownloadingResults = ref<boolean>(false)
  const isProcessingAdvancedAnalysis = ref<boolean>(false)
  const isLoadingAplosFile = ref<boolean>(false)
  const isLoadingAvailableAdvancedAnalyses = ref<boolean>(false)
  const hasErrorLoadingData = ref<boolean>(false)
  const availableAdvancedAnalysisSchemas = ref<AdvancedAnalysisSchema[]>([])

  // Reactive data for active rules
  const activeAdvancedAnalyses = reactive<AdvancedAnalysis[]>([])
  const selectedAdvancedAnalysis = ref<AdvancedAnalysis | null>(null)
  const showAdvancedAnalysisSelection = ref(false)
  const showJsonManagement = ref(false)
  const dataViewerModalOpen = ref(false)

  // --- File selection state ---
  const showFileManager = ref(false)
  const selectedFile = ref<AplosFile | null>(null)

  const onConfigViewModeTabChange = (activeMode: 'view' | 'edit') => {
    if (
      selectedAdvancedAnalysis.value == null ||
      activeAdvancedAnalysisEditConfiguration.value == null
    )
      return
    if (activeMode === 'edit') {
      const flattened = AdvancedAnalysisService.getFlattendAdvancedAnalyses([
        selectedAdvancedAnalysis.value
      ])
      activeAdvancedAnalysisEditConfiguration.value = flattened?.length ? flattened[0] : null
    }
  }

  const loadAvailableAdvancedAnalysisSchemas = async () => {
    hasErrorLoadingData.value = false
    isLoadingAvailableAdvancedAnalyses.value = true
    try {
      const advancedAnalysisSchemas = await cdnSchemaFactory.loadAvailableSchemasForCdnSchemaType(
        CdnSchemaType.AdvancedAnalysis,
        customSchemaStore
      )

      availableAdvancedAnalysisSchemas.value = advancedAnalysisSchemas
    } catch (error) {
      hasErrorLoadingData.value = true
      message.error('Failed to load available advanced analyses.')
    } finally {
      isLoadingAvailableAdvancedAnalyses.value = false
    }
  }

  const draggedAdvancedAnalysis = ref<AdvancedAnalysis | null>(null)
  const highlightedAdvancedAnalysisId = ref<string | null>(null)
  const deletingAdvancedAnalysisId = ref<string | null>(null)
  const dragOverIndex = ref<number>(-1)

  const activeAdvancedAnalysisViewMode = ref<{ mode: 'view' | 'edit' }>({ mode: 'view' })
  const activeAdvancedAnalysisEditConfiguration = ref<{} | null>(null)

  watch(
    selectedAdvancedAnalysis,
    (newVal) => {
      if (newVal) {
        // Update tracked flattented JSON for management slideout
        const flattenedAdvancedAnalyses = AdvancedAnalysisService.getFlattendAdvancedAnalyses([
          newVal
        ])
        activeAdvancedAnalysisEditConfiguration.value = flattenedAdvancedAnalyses?.length
          ? flattenedAdvancedAnalyses[0]
          : null
      }
    },
    { deep: false }
  )

  // Ensure last updated (UI) advanced analysis configuration is flattened and update
  // Json editor model ref
  const onAdvancedAnalysisViewModeTabChange = (activeMode: 'view' | 'edit') => {
    if (
      selectedAdvancedAnalysis.value == null ||
      activeAdvancedAnalysisEditConfiguration.value == null
    )
      return

    if (activeMode === 'edit') {
      const flattenedAdvancedAnalyses = AdvancedAnalysisService.getFlattendAdvancedAnalyses([
        selectedAdvancedAnalysis.value
      ])
      activeAdvancedAnalysisEditConfiguration.value = flattenedAdvancedAnalyses?.length
        ? flattenedAdvancedAnalyses[0]
        : null
    }
  }

  const addAdvancedAnalysis = (
    advancedAnalysisSchema: AdvancedAnalysisSchema
  ): AdvancedAnalysis => {
    const newAdvancedAnalysis =
      AdvancedAnalysisService.createAdvancedAnalysisFromAdvancedAnalysisSchema(
        advancedAnalysisSchema
      )

    const firstSchemaType = availableAdvancedAnalysisSchemas.value?.length
      ? availableAdvancedAnalysisSchemas.value[0]
      : null

    if (firstSchemaType == null) {
      message.error('Missing required Advanced Analysis CDN Schema content!')
    }
    // Explicitly initialize all field defaults before handing off
    newAdvancedAnalysis.configuration =
      AdvancedAnalysisService.initializeAdvancedAnalysisConfiguration(
        newAdvancedAnalysis,
        firstSchemaType!.fields ?? []
      )
    newAdvancedAnalysis.configuration.fileId = selectedFile.value!.id

    activeAdvancedAnalyses.splice(0, activeAdvancedAnalyses.length, newAdvancedAnalysis)
    selectedAdvancedAnalysis.value = newAdvancedAnalysis
    updateSharedModelOnAdvancedAnalysisChange()
    message.success(
      `Added advanced analysis: ${advancedAnalysisSchema.display_name}`,
      defaultMessageDurationSeconds
    )
    return newAdvancedAnalysis
  }

  // Select a advanced analysis to configure
  const selectAdvancedAnalysis = (advancedAnalysis: AdvancedAnalysis) => {
    selectedAdvancedAnalysis.value = advancedAnalysis
  }

  // Remove an advanced analysis
  const removeAdvancedAnalysisTimeoutMs: number = 600
  const removeAdvancedAnalysis = (advancedAnalysis: AdvancedAnalysis) => {
    // Start deletion animation
    animateAdvancedAnalysisDeletion(advancedAnalysis.id as string)
    message.success(`Removed "${advancedAnalysis.display_name}"`, defaultMessageDurationSeconds)
  }

  const notifyConfigChangedStream$ = new Subject<void>()
  const batchNotifyConfigChangedSubscription = notifyConfigChangedStream$
    .pipe(debounceTime(1800))
    .subscribe(() => {
      message.success('JSON updated', defaultMessageDurationSeconds)
    })

  // Raw Json Edits are parsed back through advancedAnalysis initialization process
  // similarly to how we import json files
  const handleJsonEditorConfigurationChange = () => {
    if (
      selectedAdvancedAnalysis.value != null &&
      activeAdvancedAnalysisEditConfiguration.value != null
    ) {
      const editConfigurationJson = JSON.stringify([activeAdvancedAnalysisEditConfiguration.value])
      const result = advancedAnalysisJsonParsingService.tryParseEntitiesFromJson(
        editConfigurationJson,
        availableAdvancedAnalysisSchemas.value
      )
      if (!result.valid) {
        message.error(
          `Error parsing JSON: ${result.errors?.join(', ')}`,
          defaultMessageDurationSeconds
        )
      } else {
        const existingAdvancedAnalysisIndex = activeAdvancedAnalyses.findIndex(
          (x) => x.id === selectedAdvancedAnalysis.value?.id
        )
        if (
          !Array.isArray(result.parsedEntities) ||
          !result.parsedEntities?.length ||
          existingAdvancedAnalysisIndex <= -1
        )
          throw new Error('Error updating advanced analysis from Json Editor value!')
        const updatedAdvancedAnalysis = (result.parsedEntities as Array<AdvancedAnalysis>)[0]
        const existingAdvancedAnalysis = activeAdvancedAnalyses[existingAdvancedAnalysisIndex]
        Object.assign(existingAdvancedAnalysis, {
          ...updatedAdvancedAnalysis,
          id: existingAdvancedAnalysis.id
        })
        selectedAdvancedAnalysis.value = existingAdvancedAnalysis
      }
    }
  }

  // Ensure validation logic for wizard nav updated based on realtime shared model
  const updateSharedModelOnAdvancedAnalysisChange = () => {
    const flattenedAdvancedAnalyses =
      AdvancedAnalysisService.getFlattendAdvancedAnalyses(activeAdvancedAnalyses)
    const flattenedAdvancedAnalysis = !!flattenedAdvancedAnalyses?.length
      ? flattenedAdvancedAnalyses[0]
      : undefined
    emit('update:advancedAnalysis', flattenedAdvancedAnalysis)
  }

  // Handle AdvancedAnalysis configuration updates
  const handleConfigurationUpdate = async (
    advancedAnalysis: AdvancedAnalysis,
    config: Record<string, any>,
    advancedAnalysisTypeChange?: boolean
  ) => {
    const advancedAnalysisSearchResults =
      AdvancedAnalysisSearchService.findAdvancedAnalysisByIdRecursive(
        activeAdvancedAnalyses,
        advancedAnalysis.id as string
      )
    if (advancedAnalysisSearchResults != null) {
      const topLevelParentAdvancedAnalysisIndex = activeAdvancedAnalyses.findIndex(
        (aa) => aa.id === advancedAnalysisSearchResults.topLevelParent?.id
      )
      if (topLevelParentAdvancedAnalysisIndex > -1) {
        // For ruleTypeChange we initialize new AdvancedAnalysis instance and
        // either splice top-level activeAdvancedAnalyses or find and set the configuration in tree of AdvancedAnalyses
        if (advancedAnalysisTypeChange === true) {
          const newAdvancedAnalysisSchema =
            AdvancedAnalysisService.tryGetAdvancedAnalysisSchemaByType(
              advancedAnalysis._type,
              availableAdvancedAnalysisSchemas.value
            )
          if (newAdvancedAnalysisSchema == null)
            throw new Error(
              `Error getting AdvancedAnalysisSchema for advancedAnalysis.type: ${advancedAnalysis.type}!`
            )
          selectedAdvancedAnalysis.value = null
          // Create new AdvancedAnalysis from schema resetting any existing metadata
          let newAdvancedAnalysis =
            AdvancedAnalysisService.createAdvancedAnalysisFromAdvancedAnalysisSchema(
              newAdvancedAnalysisSchema
            )
          // Initialize any sub-AdvancedAnalysis metadata if neccessary
          newAdvancedAnalysis.description = advancedAnalysis.configuration?.description
          newAdvancedAnalysis.configuration = { ...config }
          if (
            advancedAnalysisSearchResults.topLevelParent != null &&
            advancedAnalysisSearchResults.topLevelParent !==
              advancedAnalysisSearchResults.advancedAnalysis &&
            advancedAnalysisSearchResults.path?.length > 1
          ) {
            // Recursive search to find and update the view model
            const relativePath = advancedAnalysisSearchResults.path.slice(1)
            AdvancedAnalysisSearchService.setAdvancedAnalysisAtPath(
              advancedAnalysisSearchResults.topLevelParent,
              relativePath,
              newAdvancedAnalysis
            )
          } else {
            activeAdvancedAnalyses.splice(
              topLevelParentAdvancedAnalysisIndex,
              1,
              newAdvancedAnalysis
            )
          }
          notifyConfigChangedStream$.next()
          nextTick(() => {
            selectedAdvancedAnalysis.value =
              activeAdvancedAnalyses[topLevelParentAdvancedAnalysisIndex]
          })
        } else if (
          advancedAnalysisSearchResults.topLevelParent ===
          advancedAnalysisSearchResults.advancedAnalysis
        ) {
          // No new rule changes, just commit updated configuration
          activeAdvancedAnalyses[topLevelParentAdvancedAnalysisIndex].configuration = { ...config }
          activeAdvancedAnalyses[topLevelParentAdvancedAnalysisIndex]._type = config._type
          notifyConfigChangedStream$.next()
        }

        updateSharedModelOnAdvancedAnalysisChange()
      }
    }
  }

  // Highlight animation for moved AdvancedAnalyses
  const highlightMovedAdvancedAnalysis = (advancedAnalysisId: string) => {
    highlightedAdvancedAnalysisId.value = advancedAnalysisId
    // Clear highlight after animation completes
    setTimeout(() => {
      highlightedAdvancedAnalysisId.value = null
    }, 1500)
  }

  // Deletion animation for removed AdvancedAnalyses
  const animateAdvancedAnalysisDeletion = (advancedAnalysisId: string) => {
    deletingAdvancedAnalysisId.value = advancedAnalysisId
    // Remove rule after animation completes
    setTimeout(() => {
      activeAdvancedAnalyses.splice(
        0,
        activeAdvancedAnalyses.length,
        ...activeAdvancedAnalyses.filter((aa) => aa.id !== advancedAnalysisId)
      )
      deletingAdvancedAnalysisId.value = null
      // Clear selection if deleted advanced analysis was selected
      if (selectedAdvancedAnalysis.value?.id === advancedAnalysisId) {
        if (activeAdvancedAnalyses?.length > 0) {
          selectedAdvancedAnalysis.value = activeAdvancedAnalyses[0]
        } else {
          selectedAdvancedAnalysis.value = null
        }
      }

      updateSharedModelOnAdvancedAnalysisChange()
    }, removeAdvancedAnalysisTimeoutMs) // Shorter duration for deletion
  }

  // Show the rule selection modal
  const showAddAdvancedAnalysisModal = () => {
    showAdvancedAnalysisSelection.value = true
  }

  // Handle file selection from FileManager
  //@ts-ignore
  const handleFileSelected = (file: AplosFile, rootFile?: AplosFile) => {
    // file is selected (can be derived/cleaned) root is original source
    if (rootFile?.id && rootFile?.name) {
      selectedFile.value = { id: rootFile.id, name: rootFile.name }
      // Attach payload for column_names if present
      ;(selectedFile as any).lastPayload = rootFile
      showFileManager.value = false
      message.success(`Selected file: ${rootFile.name}`, defaultMessageDurationSeconds)
    } else {
      message.error('Invalid file selected', defaultMessageDurationSeconds)
    }
  }

  const viewFileData = async () => {
    if (selectedFile.value?.id != null) {
      dataViewerModalOpen.value = true
    }
  }

  const closeDataViewerModal = () => {
    dataViewerModalOpen.value = false
  }

  // JSON Export Functions
  const showJsonManagementDrawer = () => {
    showJsonManagement.value = true
  }

  const downloadResultsButtonDisabled = computed(
    () =>
      isProcessingAdvancedAnalysis.value ||
      !execution.value?.id ||
      execution.value?.status !== ExecutionStatusType.Succeeded
  )

  const downloadResults = async () => {
    try {
      isDownloadingResults.value = true
      await auth.initialize()
      const args = new GetExecutionOutputPackageArgs({
        userId: auth.user.id as string,
        tenantId: auth.user.tenant_id as string,
        executionId: execution.value?.id as string
      })
      const response = await executionApiService.getExecutionOutputPackage(args)
      if (!response.downloadUrl)
        throw Error('Invalid downloadUrl encountered onDownloadActionClicked')
      await downloadFile(response.downloadUrl, extractFilename(response.downloadUrl))
    } catch (error) {
      console.log(error)
      toast.error(`An error occurred trying to download the output package.`)
    } finally {
      isDownloadingResults.value = false
    }
  }

  const viewData = async (aplosFile: AplosFile | null) => {
    if (!aplosFile?.id) toast.error('Error viewing data for selected file. Missing record or Id!')

    const resolved = router.resolve({
      path: '/app/data-viewer',
      query: { fileId: aplosFile?.id }
    })
    const url = window.location.origin + resolved.href
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  // Move rule up in the list
  const moveAdvancedAnalysisUp = (advancedAnalysis: AdvancedAnalysis) => {
    const currentIndex = activeAdvancedAnalyses.findIndex((aa) => aa.id === advancedAnalysis.id)
    if (currentIndex > 0) {
      // Swap with the previous AdvancedAnalysis
      const temp = activeAdvancedAnalyses[currentIndex]
      activeAdvancedAnalyses[currentIndex] = activeAdvancedAnalyses[currentIndex - 1]
      activeAdvancedAnalyses[currentIndex - 1] = temp

      // Highlight the moved AdvancedAnalysis
      highlightMovedAdvancedAnalysis(advancedAnalysis.id as string)

      message.success(
        `Moved "${advancedAnalysis.display_name}" up to position ${currentIndex}`,
        defaultMessageDurationSeconds
      )
    }
  }

  // Move AdvancedAnalysis down in the list
  const moveAdvancedAnalysisDown = (advancedAnalysis: AdvancedAnalysis) => {
    const currentIndex = activeAdvancedAnalyses.findIndex((aa) => aa.id === advancedAnalysis.id)
    if (currentIndex < activeAdvancedAnalyses.length - 1) {
      // Swap with the next rule
      const temp = activeAdvancedAnalyses[currentIndex]
      activeAdvancedAnalyses[currentIndex] = activeAdvancedAnalyses[currentIndex + 1]
      activeAdvancedAnalyses[currentIndex + 1] = temp

      // Highlight the moved AdvancedAnalysis
      highlightMovedAdvancedAnalysis(advancedAnalysis.id as string)

      message.success(
        `Moved "${advancedAnalysis.display_name}" down to position ${currentIndex + 2}`,
        defaultMessageDurationSeconds
      )
    }
  }

  // Drag and drop handlers
  const handleDragStart = (event: DragEvent, advancedAnalysis: AdvancedAnalysis) => {
    draggedAdvancedAnalysis.value = advancedAnalysis
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', advancedAnalysis.id as string)
    }
  }

  const handleDragOver = (event: DragEvent, index: number) => {
    event.preventDefault()
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move'
    }
    dragOverIndex.value = index
  }

  const handleDragLeave = () => {
    dragOverIndex.value = -1
  }

  const handleDrop = (event: DragEvent, targetIndex: number) => {
    event.preventDefault()

    if (draggedAdvancedAnalysis.value) {
      const draggedIndex = activeAdvancedAnalyses.findIndex(
        (aa) => aa.id === draggedAdvancedAnalysis.value!.id
      )

      if (draggedIndex !== targetIndex) {
        // Remove the dragged AdvancedAnalysis from its current position
        const [movedAdvancedAnalysis] = activeAdvancedAnalyses.splice(draggedIndex, 1)
        // Insert it at the new position
        activeAdvancedAnalyses.splice(targetIndex, 0, movedAdvancedAnalysis)

        // Highlight the moved AdvancedAnalysis
        highlightMovedAdvancedAnalysis(draggedAdvancedAnalysis.value.id as string)

        message.success(
          `Moved "${draggedAdvancedAnalysis.value.display_name}" to position ${targetIndex + 1}`,
          defaultMessageDurationSeconds
        )
      }
    }

    // Clear drag state
    draggedAdvancedAnalysis.value = null
    // Remove drop zone highlighting from all cards
    document.querySelectorAll('.drop-zone-active').forEach((el) => {
      el.classList.remove('drop-zone-active')
    })
  }

  const handleDragEnd = () => {
    // Reset drag state
    draggedAdvancedAnalysis.value = null
    dragOverIndex.value = -1
  }

  const clearFileSelection = () => {
    selectedFile.value = null
    if (activeAdvancedAnalyses?.length > 0) {
      Modal.confirm({
        title: 'Confirm',
        content: 'Clear existing advanced analyses?',
        okText: 'Yes',
        cancelText: 'No',
        onOk() {
          activeAdvancedAnalyses.splice(0, activeAdvancedAnalyses.length)
          selectedAdvancedAnalysis.value = null
        },
        onCancel() {}
      })
    }
  }

  const isProcessAdvancedAnalysisDisabled = computed(() => {
    const activeAdvancedAnalysisPKAnalysis =
      activeAdvancedAnalyses?.length === 1 && activeAdvancedAnalyses[0].configuration?.calculation
    const activeAdvancedAnalysisHasConfiguredPKAnalysis =
      activeAdvancedAnalysisPKAnalysis != null &&
      Object.keys(activeAdvancedAnalysisPKAnalysis)?.length !== 0

    return (
      selectedFile.value == null ||
      isProcessingAdvancedAnalysis.value ||
      !activeAdvancedAnalysisHasConfiguredPKAnalysis
    )
  })

  const submitAdvancedAnalysisForProcessing = async () => {
    isProcessingAdvancedAnalysis.value = true

    const auth = useUser()
    await auth.initialize()

    const flattenedAdvancedAnalyses =
      AdvancedAnalysisService.getFlattendAdvancedAnalyses(activeAdvancedAnalyses)

    const args = AdvancedAnalysisService.createQueueAdvancedAnalysisArgs(
      auth.user.id as string,
      auth.user.tenant_id as string,
      selectedFile.value?.id as string,
      flattenedAdvancedAnalyses
    )

    try {
      const response = await AnalysisWizardService.runAsyncTaskWithLoading(
        'Initiating Analysis',
        props.wizardModel,
        advancedAnalysisApiService.processAdvancedAnalysis.bind(advancedAnalysisApiService),
        args
      )

      const processAdvancedAnalysesResponse = response?.data as QueueExecutionResponse
      if (!processAdvancedAnalysesResponse?.executionId)
        throw Error('An unexpected error has occurred!')
      const successMessage = 'Initiating Analysis'
      /* processAdvancedAnalysesResponse?.message?.length
          //? processAdvancedAnalysesResponse.message
          : 'Advanced Analysis queued for processing' */
      message.success(successMessage, defaultMessageDurationSeconds)

      execution.value = {
        id: processAdvancedAnalysesResponse?.executionId,
        status: ExecutionStatusType.Queued,
        startedUtc: new Date().toISOString()
      } as Execution

      // Emit initial execution step so implementor (wizard) is updated
      emit('update:advancedAnalysisExecution', execution.value)

      nextTick(() => {
        executionStatusComponent.value?.startPollingStatus()
      })
    } catch (error: any) {
      isProcessingAdvancedAnalysis.value = false
      // Normalize different possible error shapes into string messages
      let errors: string[] = ['No specific errors encountered.']

      const respErrors = error?.response?.data?.errors

      if (Array.isArray(respErrors) && respErrors.length > 0) {
        errors = respErrors.map((item: any) => {
          if (typeof item === 'string') return item
          if (item && typeof item === 'object')
            return item.error ?? item.message ?? JSON.stringify(item)
          return String(item)
        })
      } else if (error?.message) {
        // Fallback to error.message if present
        errors = [String(error.message)]
      }

      Modal.error({
        title: 'Error',
        content: h('div', { style: { marginTop: '5px' } }, [
          h(
            'p',
            { style: { marginBottom: '10px' } },
            'An error(s) was encountered initiating analysis:'
          ),
          h(
            'ul',
            errors.map((err) => h('li', { style: { marginTop: '5px' } }, err))
          )
        ]),
        okText: 'Ok',
        onOk() {}
      })
    }
  }

  //@ts-ignore
  const onExecutionStatusChanged = (status: ExecutionStatusType, execution: Execution) => {
    emit('update:advancedAnalysisExecution', execution)
  }

  const onExecutionCompleted = async (success: boolean, ex: Execution) => {
    execution.value = ex
    emit('update:advancedAnalysisExecution', ex)
    isProcessingAdvancedAnalysis.value = false
    if (success) {
      updateWizardStepStatus(WizardStepStatusType.Good, 'Good')
    } else {
      message.error('An error was encountered initiating analysis')
      updateWizardStepStatus(WizardStepStatusType.Errors, 'Error Initiating Analysis')
    }
  }

  const onJsonManagementAdvancedAnalysesApplied = () => {
    selectedAdvancedAnalysis.value = activeAdvancedAnalyses?.length
      ? activeAdvancedAnalyses[0]
      : null
    updateSharedModelOnAdvancedAnalysisChange()
  }

  // For custom or unknown types disable superfluous input tab and just rely on Json Editor Tab
  const inputTabDisabled = computed(() => {
    if (
      selectedAdvancedAnalysis.value == null ||
      selectedAdvancedAnalysis.value._type === AdvancedAnalysisService.CUSTOM_ADVANCED_ANALYSIS_TYPE
    )
      return true
    const activeSchemaType = AdvancedAnalysisService.tryGetAdvancedAnalysisSchemaByType(
      selectedAdvancedAnalysis.value._type as string,
      availableAdvancedAnalysisSchemas.value
    )
    return (
      activeSchemaType == null ||
      activeSchemaType.type === AdvancedAnalysisService.CUSTOM_ADVANCED_ANALYSIS_TYPE
    )
  })

  // Ensure we preselect the edit tab if view is disabled
  // but only if the mode is not already edit
  watch(
    inputTabDisabled,
    (newValue) => {
      if (newValue) {
        activeAdvancedAnalysisViewMode.value = { mode: 'edit' }
      } else if (activeAdvancedAnalysisViewMode.value.mode !== 'edit') {
        activeAdvancedAnalysisViewMode.value = { mode: 'view' }
      }
    },
    { immediate: true }
  )

  watch(
    () => props.analysisFile,
    async (analysisFile?: AplosFile) => {
      selectedFile.value = analysisFile as AplosFile
    },
    { immediate: true }
  )

  const updateWizardStepStatus = (statusType?: WizardStepStatusType, statusMessage?: string) => {
    if (props.wizardModel != null) {
      const stepStatusType =
        statusType != null
          ? statusType
          : props.analysisFile == null
            ? WizardStepStatusType.Warnings
            : WizardStepStatusType.Good

      let stepStatusMessage = statusMessage

      if (stepStatusMessage == null) {
        if (props.analysisFile == null) stepStatusMessage = 'Missing input dataset'
      }

      wizardNavigationService.trySetWizardActiveStepStatus(
        props.wizardModel,
        stepStatusType,
        stepStatusMessage
      )
    }
  }

  onMounted(async () => {
    isInitializing.value = true
    await loadAvailableAdvancedAnalysisSchemas()

    // Apply the watch inside the onMounted so it runs after we have loaded schemas
    // When in singleconfig mode, require the input anaylsisConfig prop and populate activeAnalysisConfigs
    watch(
      () => [props.mode, props.advancedAnalysis, props.analysisFile],
      //@ts-ignore
      ([mode, inputAdvancedAnalysis, inputAnalysisFile]) => {
        if (mode === 'singleconfig' || mode == 'multiconfig') {
          // single mode requires an advanced analysis prop
          if (props.advancedAnalysis && props.analysisFile) {
            activeAdvancedAnalyses.splice(0, activeAdvancedAnalyses.length)
            selectedAdvancedAnalysis.value = null

            if (props.advancedAnalysis.type == null) {
              const firstSchemaType = availableAdvancedAnalysisSchemas.value?.length
                ? availableAdvancedAnalysisSchemas.value[0]
                : null

              if (firstSchemaType == null) {
                message.error('Missing required Advanced Analysis CDN Schema content!')
              } else {
                props.advancedAnalysis.type = firstSchemaType.type
                emit('update:advancedAnalysis', props.advancedAnalysis)
              }
            }

            // Initialize configuration from schema so we can build UI
            const initializedAdvancedAnalysis = advancedAnalysisJsonParsingService.initializeEntity(
              props.advancedAnalysis,
              availableAdvancedAnalysisSchemas.value,
              1
            )
            activeAdvancedAnalyses.splice(
              0,
              activeAdvancedAnalyses.length,
              initializedAdvancedAnalysis
            )

            if (activeAdvancedAnalyses.length > 0) {
              selectedAdvancedAnalysis.value = activeAdvancedAnalyses[0]
            }
          } else if (props.analysisFile != null) {
            const firstSchemaType = availableAdvancedAnalysisSchemas.value?.length
              ? availableAdvancedAnalysisSchemas.value[0]
              : null

            if (firstSchemaType == null) {
              message.error('Missing required Advanced Analysis CDN Schema content!')
            } else {
              const newAdvancedAnalysis =
                AdvancedAnalysisService.createAdvancedAnalysisFromAdvancedAnalysisSchema(
                  firstSchemaType
                )

              // Explicitly initialize all field defaults before handing off
              newAdvancedAnalysis.configuration =
                AdvancedAnalysisService.initializeAdvancedAnalysisConfiguration(
                  newAdvancedAnalysis,
                  firstSchemaType.fields ?? []
                )
              newAdvancedAnalysis.configuration.fileId = props.analysisFile.id // now safe to add

              activeAdvancedAnalyses.push(newAdvancedAnalysis)
              selectedAdvancedAnalysis.value = newAdvancedAnalysis
              emit('update:advancedAnalysis', props.advancedAnalysis)
            }
          }
        }
      },
      { immediate: true }
    )

    watch(
      activeAdvancedAnalyses,
      () => {
        updateWizardStepStatus(WizardStepStatusType.Changes, 'Unsaved changes')
      },
      { immediate: false }
    )

    updateWizardStepStatus()
    isInitializing.value = false
  })

  onBeforeUnmount(() => {
    batchNotifyConfigChangedSubscription?.unsubscribe()
  })

  // Add activeAdvancedAnalysisTabKey for dynamic tab selection
  const activeAdvancedAnalysisTabKey = ref<string>('view')

  // Add uiFieldGroups computed property
  const uiFieldGroups = computed(() => {
    return AdvancedAnalysisUiService.getAdvancedAnalysisSchemaUiFieldGroups(
      selectedAdvancedAnalysis.value?.schema
    )
  })

  // Ensure first tab is selected on update of selected rule
  // persist JSON tab selection if already active
  watch(
    [selectedAdvancedAnalysis, uiFieldGroups],
    ([advancedAnalysis, groups]) => {
      if (advancedAnalysis != null && groups != null && groups.length > 0) {
        // If on 'edit' tab, do not change
        if (activeAdvancedAnalysisTabKey.value === 'edit') return
        // If the current tab key is not in the group, or empty, set to first group
        if (
          !groups.some(
            (g: AdvancedAnalysisSchemaUiFieldGroup) => g.name === activeAdvancedAnalysisTabKey.value
          )
        ) {
          activeAdvancedAnalysisTabKey.value = groups[0].name
        }
      }
    },
    { immediate: true }
  )

  // --- Add this watcher to reset tab selection on selectedAdvancedAnalysis change ---
  watch(selectedAdvancedAnalysis, (newAdvancedAnalysis, oldAdvancedAnalysis) => {
    // Only reset if the AdvancedAnalysis actually changed (not just updated in place)
    if (
      props.mode !== 'singleconfig' &&
      newAdvancedAnalysis &&
      newAdvancedAnalysis !== oldAdvancedAnalysis
    ) {
      // If uiFieldGroups exist and have at least one group, select the first group tab
      if (uiFieldGroups.value && uiFieldGroups.value.length > 0) {
        activeAdvancedAnalysisTabKey.value = uiFieldGroups.value[0].name
      } else if (!inputTabDisabled.value) {
        // If Input tab is not disabled, select Input tab
        activeAdvancedAnalysisViewMode.value = { mode: 'view' }
      } else {
        // Otherwise, select JSON tab
        activeAdvancedAnalysisViewMode.value = { mode: 'edit' }
      }
    }
  })

  //@ts-ignore
  const goToWizardStepByType = (stepType: AdvancedAnalysisWizardStepType) => {
    if (props.wizardModel != null) {
      wizardNavigationService.setActiveWizardStepByType(stepType, props.wizardModel)
    }
  }

  // Add computed helper to detect multi vs single mode
  const isMulticonfig = computed(() => props.mode === 'multiconfig')

  const { is2XLargeTierOrLess } = useWindowSize()
  // derive live indicator from shared layoutMode so component follows active AppLayoutMode
  const { layoutMode } = useAppLayoutMode()
  const isFitToBrowser = computed(() => layoutMode.value === AppLayoutMode.FitToBrowser)

  defineExpose({
    activeAdvancedAnalyses,
    addAdvancedAnalysis,
    removeAdvancedAnalysis,
    removeAdvancedAnalysisTimeoutMs,
    moveAdvancedAnalysisUp,
    moveAdvancedAnalysisDown
  })
</script>

<template>
  <div
    :class="[
      'analysisconfig-container',
      'h-full',
      'min-h-0',
      {
        'fit-to-browser': isFitToBrowser,
        'analysisconfig-fill': isFitToBrowser
      }
    ]"
  >
    <Splitter :style="isFitToBrowser ? { height: 'unset' } : {}">
      <!-- Left panel only shows in multiconfig mode -->
      <SplitterPanel v-if="isMulticonfig" :size="30" style="min-width: 350px">
        <div class="flex h-full flex-col border-r-2 shadow-sm">
          <div class="border-b p-4 shadow-sm">
            <div class="mb-3">
              <label class="mb-1 block text-sm font-medium">Source File</label>
              <div class="mb-2 flex items-center gap-2">
                <Button
                  type="dashed"
                  class="file-select-button min-w-0 flex-1 text-left"
                  block
                  :disabled="
                    isLoadingAplosFile || isLoadingAvailableAdvancedAnalyses || showLoading
                  "
                  @click="showFileManager = true"
                  :title="selectedFile ? 'Change selected file' : 'Select a file'"
                >
                  <span class="file-select-label block w-full truncate">
                    {{ selectedFile?.name ?? 'Select Input File' }}
                  </span>
                </Button>

                <a-tooltip>
                  <template #title>
                    {{ 'Loading file...' }}
                  </template>
                  <Loader2 v-if="isLoadingAplosFile" class="mr-2 size-4 animate-spin" />
                </a-tooltip>
                <a-tooltip>
                  <template #title>
                    {{ 'View Data' }}
                  </template>
                  <Button
                    v-if="selectedFile && !isLoadingAplosFile"
                    type="default"
                    shape="circle"
                    size="large"
                    class="flex items-center justify-center"
                    @click="viewFileData"
                    title="View Data"
                    tooltip="View Data"
                  >
                    <FileSearchOutlined class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>

                <a-tooltip>
                  <template #title>
                    {{ 'Clear selection' }}
                  </template>
                  <Button
                    v-if="selectedFile && !isLoadingAplosFile"
                    type="default"
                    shape="circle"
                    size="large"
                    danger
                    class="flex items-center justify-center"
                    @click="clearFileSelection"
                    title="Clear Selection"
                    tooltip="Clear Selection"
                  >
                    <Trash2 class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>
              </div>
            </div>
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold">Analysis Configs</h3>
                <p class="mt-1 text-sm">
                  {{ activeAdvancedAnalyses.length }}
                  {{ activeAdvancedAnalyses.length === 1 ? 'config' : 'configs' }} configured
                </p>
              </div>
              <div class="flex items-center gap-2 self-center">
                <a-tooltip>
                  <template #title>
                    {{ 'Add New Advanced Analysis' }}
                  </template>
                  <Button
                    :disabled="selectedFile == null || activeAdvancedAnalyses?.length >= 1"
                    type="primary"
                    shape="circle"
                    size="large"
                    class="flex items-center justify-center"
                    @click="showAddAdvancedAnalysisModal"
                    title="Add New"
                  >
                    <PlusOutlined class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>
                <a-tooltip>
                  <template #title>
                    {{ 'View/Export JSON' }}
                  </template>
                  <Button
                    type="default"
                    shape="circle"
                    size="large"
                    class="flex items-center justify-center"
                    @click="showJsonManagementDrawer"
                    title="View/Export JSON"
                    :disabled="
                      !selectedFile ||
                      activeAdvancedAnalyses.length === 0 ||
                      isProcessingAdvancedAnalysis
                    "
                  >
                    <CodeOutlined class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>
              </div>
            </div>
          </div>

          <div class="configs-list-container flex-1 p-4">
            <div class="configs-scroll-area min-h-0 flex-1 overflow-y-auto px-1 py-2">
              <div
                v-if="activeAdvancedAnalyses.length === 0"
                class="flex h-full flex-col items-center justify-start py-12 text-center"
              >
                <div class="mb-6">
                  <div
                    class="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[var(--from-blue-gradient)] to-[var(--to-blue-gradient)]"
                  >
                    <svg
                      class="h-10 w-10 text-blue-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                  </div>
                </div>
                <h3 class="mb-2 text-lg font-semibold">No Advanced Analysis Yet</h3>
                <div
                  class="mt-8 max-w-md rounded-lg border bg-[var(--card-lighter)] p-4 dark:bg-card"
                >
                  <h4 class="mb-2 text-sm font-medium text-blue-900 dark:text-white">
                    💡 Quick Tips
                  </h4>
                  <ul class="space-y-1 text-xs text-blue-800 dark:text-white">
                    <li>• Advanced Analyses are created after selecting a file</li>
                    <li>• Use the + button to add a config type</li>
                    <li>• Configure each item in the right panel</li>
                  </ul>
                </div>
              </div>
              <div v-else class="space-y-1.5 px-1 pb-5">
                <Card
                  v-for="(cfg, index) in activeAdvancedAnalyses"
                  :key="cfg.id"
                  :class="[
                    'group cursor-pointer bg-[var(--card)] transition-all duration-200 hover:shadow-md dark:bg-[var(--card-light)]',
                    {
                      'ring-2 ring-blue-500': selectedAdvancedAnalysis?.id === cfg.id,
                      'opacity-50': draggedAdvancedAnalysis?.id === cfg.id,
                      'border-green-300 bg-[var(--card-light)] dark:bg-[var(--card-lighter)]':
                        dragOverIndex === index,
                      'config-moved-highlight': highlightedAdvancedAnalysisId === cfg.id,
                      'config-deleting': deletingAdvancedAnalysisId === cfg.id
                    }
                  ]"
                  :bodyStyle="{ padding: '18px' }"
                  draggable="true"
                  @click="selectAdvancedAnalysis(cfg)"
                  @dragstart="handleDragStart($event, cfg)"
                  @dragover="handleDragOver($event, index)"
                  @dragleave="handleDragLeave"
                  @drop="handleDrop($event, index)"
                  @dragend="handleDragEnd"
                >
                  <div>
                    <div class="flex items-start justify-between">
                      <div class="mr-3 flex items-center">
                        <HolderOutlined class="cursor-grab text-gray-400 active:cursor-grabbing" />
                      </div>
                      <div class="min-w-0 flex-1">
                        <h4 class="truncate text-sm font-medium leading-tight">
                          {{ cfg.display_name }}
                        </h4>
                        <p class="mt-0 line-clamp-1 text-xs text-gray-600">
                          {{ cfg.configuration?.description ?? '&nbsp;' }}
                        </p>
                      </div>
                      <div
                        class="ml-1.5 flex flex-row gap-1 opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        <Button
                          type="text"
                          size="small"
                          :disabled="activeAdvancedAnalyses.findIndex((p) => p.id === cfg.id) === 0"
                          class="flex h-5 w-5 items-center justify-center"
                          @click.stop="moveAdvancedAnalysisUp(cfg)"
                        >
                          <ArrowUpOutlined class="text-xs" />
                        </Button>
                        <Button
                          type="text"
                          size="small"
                          :disabled="
                            activeAdvancedAnalyses.findIndex((p) => p.id === cfg.id) ===
                            activeAdvancedAnalyses.length - 1
                          "
                          class="flex h-5 w-5 items-center justify-center"
                          @click.stop="moveAdvancedAnalysisDown(cfg)"
                        >
                          <ArrowDownOutlined class="text-xs" />
                        </Button>
                        <Button
                          type="link"
                          size="small"
                          danger
                          class="flex h-8 w-8 items-center justify-center"
                          @click.stop="removeAdvancedAnalysis(cfg)"
                        >
                          <Trash2 class="text-base" style="line-height: 1; margin: 0" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </SplitterPanel>

      <!-- Right panel takes full width when single mode -->
      <SplitterPanel style="flex-basis: calc(100% - 4px)" class="bg-white dark:bg-[var(--card)]">
        <div :class="['flex', 'flex-col', { 'h-full': isFitToBrowser }]">
          <div
            class="flex items-center border-b bg-[var(--card-lighter)] p-4 dark:bg-[var(--card)]"
          >
            <div class="mr-5 min-w-0 flex-1">
              <h3 class="text-nowrap text-xs font-semibold sm:text-sm md:text-base lg:text-base">
                {{
                  selectedAdvancedAnalysis
                    ? `Configure: ${selectedAdvancedAnalysis.display_name}`
                    : 'Advanced Analysis'
                }}
              </h3>
              <p class="mt-100 text-xs sm:text-sm md:text-sm lg:text-sm">
                {{
                  selectedFile != null
                    ? 'Configure the selected item below'
                    : 'Select an analysis to begin configuring'
                }}
              </p>
            </div>
            <div class="flex items-center gap-1">
              <div class="mr-3">
                <ExecutionStatusComponent
                  :execution="execution"
                  :show-run-time="true"
                  ref="executionStatusComponent"
                  @notify-execution-status-changed="onExecutionStatusChanged"
                  @notify-execution-complete="onExecutionCompleted"
                />
              </div>

              <!-- Download Results -->
              <a-tooltip>
                <template #title>
                  {{
                    selectedFile == null || !activeAdvancedAnalyses?.length
                      ? 'Initiate Analysis to download results.'
                      : 'Download Results'
                  }}
                </template>
                <Button
                  v-if="is2XLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  :disabled="downloadResultsButtonDisabled"
                  @click="downloadResults"
                >
                  <span class="ant-button-flex-content">
                    <Loader2 v-if="isDownloadingResults" class="size-4 animate-spin" />
                    <Download
                      v-else
                      class="size-5"
                      stroke-width="1.5"
                      :class="{
                        color: !downloadResultsButtonDisabled ? 'bg-[var(--background)]' : undefined
                      }"
                    />
                  </span>
                </Button>

                <Button
                  v-else
                  type="primary"
                  :disabled="downloadResultsButtonDisabled"
                  @click="downloadResults"
                >
                  <span class="ant-button-flex-content">
                    <Loader2 v-if="isDownloadingResults" class="mr-2 size-4 animate-spin" />
                    <Download
                      v-else
                      class="size-5"
                      stroke-width="1.5"
                      :class="{
                        color: !downloadResultsButtonDisabled ? 'bg-[var(--background)]' : undefined
                      }"
                    />
                    {{ 'Download Results' }}
                  </span>
                </Button>
              </a-tooltip>

              <!-- View Data -->
              <a-tooltip>
                <template #title>
                  {{ selectedFile == null ? 'Select a dataset to view' : 'View Data' }}
                </template>
                <Button
                  v-if="is2XLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  :disabled="!selectedFile"
                  @click="viewData(selectedFile)"
                >
                  <span class="ant-button-flex-content">
                    <FileSearchOutlined
                      class="size-4"
                      stroke-width="1.5"
                      :class="{
                        color: !selectedFile ? 'bg-[var(--background)]' : undefined
                      }"
                    />
                  </span>
                </Button>

                <Button
                  v-else
                  type="primary"
                  :disabled="!selectedFile"
                  @click="viewData(selectedFile)"
                >
                  <span class="ant-button-flex-content">
                    <FileSearchOutlined
                      class="size-5"
                      stroke-width="1.5"
                      :class="{
                        color: !selectedFile ? 'bg-[var(--background)]' : undefined
                      }"
                    />
                    {{ 'View Data' }}
                  </span>
                </Button>
              </a-tooltip>

              <!-- Execute Advanced Analysis -->
              <a-tooltip
                placement="left"
                :open="isProcessAdvancedAnalysisDisabled ? false : undefined"
              >
                <template #title>
                  {{
                    selectedFile == null || !activeAdvancedAnalyses?.length
                      ? 'Select a file and configure at minumum a PK Analysis to Initiate Analysis.'
                      : !isProcessingAdvancedAnalysis
                        ? 'Initiate Analysis'
                        : 'Initiating Analysis'
                  }}
                </template>
                <Button
                  v-if="is2XLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  :disabled="isProcessAdvancedAnalysisDisabled"
                  @click="submitAdvancedAnalysisForProcessing"
                >
                  <Loader2 v-if="isProcessingAdvancedAnalysis" class="size-4 animate-spin" />
                  <CloudCog v-else class="h-4 w-4" />
                </Button>
                <Button
                  v-else
                  type="primary"
                  :disabled="isProcessAdvancedAnalysisDisabled"
                  @click="submitAdvancedAnalysisForProcessing"
                >
                  <span class="ant-button-flex-content">
                    <Loader2 v-if="isProcessingAdvancedAnalysis" class="mr-2 size-4 animate-spin" />
                    <CloudCog v-else class="h-4 w-4" />
                    {{ 'Initiate Analysis' }}
                  </span>
                </Button>
              </a-tooltip>

              <!-- View/Export JSON-->
              <a-tooltip placement="left">
                <template #title>
                  {{ 'View/Export JSON' }}
                </template>
                <Button
                  v-if="is2XLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  @click="showJsonManagement = true"
                  title="View/Export JSON"
                  :disabled="
                    (!selectedFile && activeAdvancedAnalyses.length === 0) ||
                    isProcessingAdvancedAnalysis
                  "
                >
                  <CodeOutlined class="h-4 w-4" />
                </Button>
                <Button
                  v-else
                  :type="selectedFile && activeAdvancedAnalyses.length !== 0 ? 'button' : 'default'"
                  :class="{ 'small-button': selectedFile && activeAdvancedAnalyses.length !== 0 }"
                  @click="showJsonManagement = true"
                  title="View/Export JSON"
                  :disabled="
                    (!selectedFile && activeAdvancedAnalyses.length === 0) ||
                    isProcessingAdvancedAnalysis
                  "
                >
                  <span class="ant-button-flex-content">
                    <CodeOutlined class="h-4 w-4" />
                    {{ 'View/Export JSON' }}
                  </span>
                </Button>
              </a-tooltip>
            </div>
          </div>

          <div
            v-if="(isLoadingAvailableAdvancedAnalyses || showLoading) && !wizardModel"
            class="flex h-full items-center justify-center"
          >
            <div :class="['py-8', 'text-center', { 'mt-[150px]': !isFitToBrowser }]">
              <Spin size="large" />
              <p class="mt-2 text-gray-500">Loading configurations...</p>
            </div>
          </div>

          <div v-else class="flex min-h-0 flex-1 flex-col p-4">
            <p
              v-if="mode === 'multiconfig' && selectedFile == null && !hasErrorLoadingData"
              class="mt-8 text-center text-sm text-gray-600"
            >
              Select a file to begin configuring analysis
            </p>

            <p
              v-else-if="
                mode === 'singleconfig' &&
                !isInitializing &&
                (props.analysisFile == null || selectedAdvancedAnalysis == null)
              "
              class="mt-8 text-center text-sm text-red-600"
            >
              An analysis configuration and file are required to add/edit a configuration.
            </p>

            <div v-else>
              <div
                v-if="
                  selectedAdvancedAnalysis != null && uiFieldGroups != null && uiFieldGroups?.length
                "
                class="min-h-0 flex-1"
              >
                <a-tabs
                  :key="selectedAdvancedAnalysis?.id"
                  v-model:activeKey="activeAdvancedAnalysisTabKey"
                  @change="onAdvancedAnalysisViewModeTabChange"
                  :destroyInactiveTabPane="true"
                >
                  <a-tab-pane
                    v-for="fieldGroup in uiFieldGroups"
                    :key="fieldGroup.name"
                    :tab="fieldGroup.name"
                  >
                    <AdvancedAnalysisConfiguration
                      :configuration-level="1"
                      :advanced-analysis="selectedAdvancedAnalysis"
                      :root-advanced-analysis="selectedAdvancedAnalysis"
                      :ui-field-group="fieldGroup"
                      @configuration-updated="handleConfigurationUpdate"
                      :advanced-analysis-schemas="availableAdvancedAnalysisSchemas"
                      :show-description="false"
                      :is-condensed-layout="true"
                      :is-processing-advanced-analysis="isProcessingAdvancedAnalysis"
                    />
                  </a-tab-pane>

                  <a-tab-pane
                    v-if="showJsonTab"
                    key="edit"
                    tab="JSON"
                    :disabled="
                      AdvancedAnalysisTypeGuardService.isPayloadTemplateAdvancedAnaylsisSchema(
                        selectedAdvancedAnalysis?.schema
                      )
                    "
                  >
                    <div class="mt-[20px]">
                      <JsonAdvancedAnalysisConfiguration
                        v-if="activeAdvancedAnalysisEditConfiguration != null"
                        :key="selectedAdvancedAnalysis?.id"
                        :advanced-analysis-schemas="availableAdvancedAnalysisSchemas"
                        v-model="activeAdvancedAnalysisEditConfiguration"
                        :editor-schema="
                          AdvancedAnalysisJsonConfigurationService.getJsonEditorSchemaForAdvancedAnalysis()
                        "
                        :place-holder="'Configure JSON'"
                        :configuration-level="1"
                        @update:modelValue="handleJsonEditorConfigurationChange"
                        :allow-type-change="false"
                      />
                    </div>
                  </a-tab-pane>
                </a-tabs>
              </div>

              <a-tabs
                v-else-if="selectedAdvancedAnalysis != null"
                @change="onConfigViewModeTabChange"
                :key="selectedAdvancedAnalysis?.id"
                v-model:activeKey="activeAdvancedAnalysisViewMode.mode"
                :destroyInactiveTabPane="false"
              >
                <a-tab-pane key="view" tab="Input" :disabled="inputTabDisabled">
                  <AdvancedAnalysisConfiguration
                    :configuration-level="1"
                    :advanced-analysis="selectedAdvancedAnalysis"
                    :root-advanced-analysis="selectedAdvancedAnalysis"
                    @configuration-updated="handleConfigurationUpdate"
                    :advanced-analysis-schemas="availableAdvancedAnalysisSchemas"
                    :show-description="true"
                    :is-condensed-layout="true"
                    :is-processing-advanced-analysis="isProcessingAdvancedAnalysis"
                  />
                </a-tab-pane>
                <a-tab-pane
                  key="edit"
                  tab="JSON"
                  :disabled="
                    AdvancedAnalysisTypeGuardService.isPayloadTemplateAdvancedAnaylsisSchema(
                      selectedAdvancedAnalysis?.schema
                    )
                  "
                >
                  <div class="mt-[20px]">
                    <JsonAdvancedAnalysisConfiguration
                      v-if="activeAdvancedAnalysisEditConfiguration != null"
                      :key="selectedAdvancedAnalysis?.id"
                      :advanced-analysis-schemas="availableAdvancedAnalysisSchemas"
                      v-model="activeAdvancedAnalysisEditConfiguration"
                      :editor-schema="
                        AdvancedAnalysisJsonConfigurationService.getJsonEditorSchemaForAdvancedAnalysis()
                      "
                      :place-holder="'Configure JSON'"
                      :configuration-level="1"
                      @update:modelValue="handleJsonEditorConfigurationChange"
                      :allow-type-change="false"
                    />
                  </div>
                </a-tab-pane>
              </a-tabs>
            </div>
          </div>
        </div>
      </SplitterPanel>
    </Splitter>

    <AdvancedAnalysisSelectionModal
      v-model:visible="showAdvancedAnalysisSelection"
      :advanced-analysis-schemas="availableAdvancedAnalysisSchemas"
      @advanced-analysis-selected="addAdvancedAnalysis"
    />

    <Modal
      v-model:open="showFileManager"
      title="Select a File"
      width="80%"
      :bodyStyle="{ height: '60vh' }"
      :footer="null"
      centered
      destroyOnClose
    >
      <div style="overflow-y: auto; height: 60vh">
        <FileManager
          @file-selected="handleFileSelected"
          :show-header="false"
          :selectable="false"
          :show-actions="false"
          title="Select a file for analysis"
          description="Click on a file to select it"
        />
      </div>
    </Modal>

    <Drawer
      v-model:open="showJsonManagement"
      :destroy-on-close="true"
      title="Advanced Analysis JSON Viewer"
      placement="right"
      :width="800"
      :closable="true"
      :mask-closable="true"
    >
      <AdvancedAnalysisJsonManagement
        v-if="selectedFile != null && availableAdvancedAnalysisSchemas != null"
        :key="selectedAdvancedAnalysis?.id"
        :advanced-analysis-schemas="availableAdvancedAnalysisSchemas"
        :file-id="selectedFile?.id!"
        :advanced-analyses="activeAdvancedAnalyses"
        :visibility-changed="showJsonManagement"
        :advanced-analysis-json-parsing-service="advancedAnalysisJsonParsingService"
        @configs-applied="onJsonManagementAdvancedAnalysesApplied"
      />
    </Drawer>

    <!-- Data Viewer Modal -->
    <FullScreenModal
      :open="dataViewerModalOpen"
      title="Data Viewer"
      :description="selectedFile?.name"
      @close="closeDataViewerModal"
    >
      <DataViewer :fileId="selectedFile?.id || ''" />
    </FullScreenModal>
  </div>
</template>

<style scoped>
  .analysisconfig-container {
    display: flex;
    flex-direction: column;
    min-height: 0;
    /* Allow component to fill available space via flex but don't force parent to grow */
  }

  /* apply flex fill only when layout mode requires filling the browser viewport */
  .analysisconfig-fill {
    flex: 1 1 0;
  }

  .file-select-button {
    text-align: center;
    flex: 1 1 0;
    min-width: 0;
  }
  .file-select-button .ant-btn-inner,
  .file-select-button .file-select-label {
    display: block;
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  /* left list container becomes a flex column and will handle scrolling itself */
  .configs-list-container {
    display: flex;
    flex-direction: column;
    /* default (FitToContent): let content size naturally and allow vertical growth */
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
  }
  /* full-height behavior only when fit-to-browser */
  .fit-to-browser .configs-list-container {
    height: 100%;
    max-height: 100%;
    overflow-y: auto;
  }

  .configs-scroll-area {
    min-height: 0;
    overflow: visible;
  }
  :deep(.p-splitter) {
    border: none;
    height: auto;
  }
  .fit-to-browser :deep(.p-splitter) {
    height: 100%;
  }
  /* Only apply full-height ant-tabs behavior when FitToBrowser is active */
  .fit-to-browser :deep(.ant-tabs) {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }
  .fit-to-browser :deep(.ant-tabs .ant-tabs-nav) {
    flex: 0 0 auto;
  }
  .fit-to-browser :deep(.ant-tabs .ant-tabs-content-holder),
  .fit-to-browser :deep(.ant-tabs .ant-tabs-content) {
    flex: 1 1 auto;
    min-height: 0 !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch;
  }
  .fit-to-browser :deep(.ant-tabs .ant-tabs-tabpane) {
    min-height: 0;
    height: 100%;
    overflow-y: auto;
  }
</style>
